

import { useEffect, useState } from "react"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"
import { useModalStore } from "@/hooks/stores/modalStore";

type Crumb = {
  name: string;
  href?: string;
  icon?: React.ElementType; // Contoh: Home, Users, etc
};
interface DashboardLayoutProps {
  breadcrumb?: Crumb[];
  toolbarRight?: React.ReactNode;
  children: React.ReactNode;
}

export default function DashboardLayout({
  breadcrumb = [],
  toolbarRight,
  children,
}: DashboardLayoutProps) {
  const [menuWidth, setMenuWidth] = useState("98vw")
  const { showModal, closeModal } = useModalStore();

  useEffect(() => {
    const handleResize = () => {
      const adminMenu = document.getElementById('adminmenuwrap')
      if (adminMenu) {
        const width = `calc(100vw - ${adminMenu.offsetWidth + 20}px)`
        setMenuWidth(width)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    // Listen for WordPress menu toggle
    const observer = new MutationObserver(handleResize)
    const adminMenu = document.getElementById('adminmenuwrap')
    if (adminMenu) {
      observer.observe(adminMenu, { attributes: true, subtree: true })
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [])

  return (
    <>

      {breadcrumb.length > 0 && (
        <div className="flex h-16 shrink-0 justify-between items-center gap-2 border-b px-10">
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumb.map((item, index) => {
                const Icon = item.icon;

                const isLast = index === breadcrumb.length - 1;
                const showSeparator = !isLast;

                return (
                  <div key={index} className="flex items-center gap-1">
                    <BreadcrumbItem className={index === 0 ? "hidden md:!flex items-center gap-1" : ""}>
                      {item.href ? (
                        <BreadcrumbLink href={item.href}>
                          {Icon && <Icon className="w-4 h-4 mr-1" />}
                          {item.name}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage className="flex items-center gap-1">
                          {Icon && <Icon className="w-4 h-4 mr-1" />}
                          {item.name}
                        </BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {showSeparator && (
                      <BreadcrumbSeparator className="hidden md:!block" />
                    )}
                  </div>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
          {toolbarRight}
        </div>
      )}
      <div className="space-y-6 md:px-10 pb-16 md:block dark:bg-gray-900 overflow-x-scroll flex-1" style={{ width: menuWidth }}>
        {/* <div className="space-y-0.5">
          <h2 className="text-2xl font-bold tracking-tight dark:text-white">Leads</h2>
          <p className="text-muted-foreground">
            Manage your account Leads and set e-mail preferences.
          </p>
        </div> */}
        {/* <Separator className="my-6" /> */}
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0">
          <div className="flex-1">
            <div className="relative w-full">
              <div>{children}</div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-md border-0 shadow-2xl overflow-hidden p-0">
          {/* Gradient Header Background */}
          <div className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 px-6 pt-8 pb-20">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12 blur-xl" />

            {/* Icon */}
            <div className="relative flex justify-center mb-4">
              <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                <svg
                  fill="white"
                  height="48px"
                  width="48px"
                  viewBox="0 0 370.04 370.04"
                  xmlns="http://www.w3.org/2000/svg"
                  className="drop-shadow-md"
                >
                  <path d="M341.668,314.412c0,0-41.071-70.588-48.438-83.248c8.382-2.557,17.311-4.815,21.021-11.221 c6.183-10.674-4.823-28.184-1.933-39.625c2.977-11.775,20.551-21.964,20.551-33.933c0-11.661-18.169-25.284-21.148-36.99 c-2.91-11.439,8.063-28.968,1.86-39.629c-6.203-10.662-26.864-9.786-35.369-17.97c-8.751-8.422-8.724-29.028-19.279-34.672 c-10.598-5.665-27.822,5.784-39.589,3.072C207.711,17.515,197.318,0,185.167,0c-12.331,0-31.944,19.868-35.02,20.583 c-11.761,2.734-29.007-8.687-39.594-2.998c-10.545,5.663-10.48,26.271-19.215,34.707c-8.491,8.199-29.153,7.361-35.337,18.035 c-6.183,10.672,4.823,28.178,1.934,39.625c-2.897,11.476-21.083,23.104-21.083,36.376c0,11.97,17.618,22.127,20.613,33.896 c2.911,11.439-8.062,28.966-1.859,39.631c3.377,5.805,11.039,8.188,18.691,10.479c0.893,0.267,2.582,1.266,1.438,2.933 c-5.235,9.036-47.37,81.755-47.37,81.755c-3.352,5.784-0.63,10.742,6.047,11.023l32.683,1.363 c6.677,0.281,15.053,5.133,18.617,10.786l17.44,27.674c3.564,5.653,9.219,5.547,12.57-0.236c0,0,48.797-84.246,48.817-84.27 c0.979-1.144,1.963-0.909,2.434-0.509c5.339,4.546,12.782,9.081,18.994,9.081c6.092,0,11.733-4.269,17.313-9.03 c0.454-0.387,1.559-1.18,2.367,0.466c0.013,0.026,48.756,83.811,48.756,83.811c3.36,5.776,9.016,5.874,12.569,0.214 l17.391-27.707c3.554-5.657,11.921-10.528,18.598-10.819l32.68-1.424C342.315,325.152,345.028,320.187,341.668,314.412z M239.18,238.631c-36.136,21.023-79.511,18.77-112.641-2.127c-48.545-31.095-64.518-95.419-35.335-145.788 c29.516-50.95,94.399-68.928,145.808-40.929c0.27,0.147,0.537,0.299,0.805,0.449c0.381,0.211,0.761,0.425,1.14,0.641 c15.86,9.144,29.613,22.415,39.461,39.342C308.516,141.955,290.915,208.533,239.18,238.631z M230.916,66.103c-0.15-0.087-0.302-0.168-0.452-0.254C203.002,49.955,168,48.793,138.665,65.86 c-43.532,25.326-58.345,81.345-33.019,124.876c7.728,13.284,18.318,23.888,30.536,31.498c1.039,0.658,2.09,1.305,3.164,1.927 c43.579,25.247,99.568,10.333,124.814-33.244C289.405,147.338,274.495,91.35,230.916,66.103z M241.818,137.344l-15.259,14.873 c-4.726,4.606-7.68,13.698-6.563,20.203l3.602,21.001c1.116,6.505-2.75,9.314-8.592,6.243l-18.861-9.916 c-5.842-3.071-15.401-3.071-21.243,0l-18.86,9.916c-5.842,3.071-9.709,0.262-8.593-6.243l3.602-21.001 c1.116-6.505-1.838-15.597-6.564-20.203l-15.258-14.873c-4.727-4.606-3.249-9.152,3.282-10.102l21.086-3.064 c6.531-0.949,14.265-6.568,17.186-12.486l9.43-19.107c2.921-5.918,7.701-5.918,10.621,0l9.431,19.107 c2.921,5.918,10.654,11.537,17.186,12.486l21.086,3.064C245.067,128.192,246.544,132.738,241.818,137.344z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <DialogTitle className="text-3xl font-bold text-white text-center mb-2 relative">
              Unlock More Features
            </DialogTitle>
          </div>

          {/* Content Section */}
          <div className="px-6 -mt-12 relative">
            {/* Feature Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 mb-6 border border-gray-100 dark:border-gray-800">
              <DialogDescription className="text-center text-gray-600 dark:text-gray-400 text-base leading-relaxed">
                You've reached the limit of your <span className="font-semibold text-gray-900 dark:text-gray-100">Free Plan</span>.
                <br />Upgrade to <span className="font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Pro</span> to unlock unlimited conversions and advanced features.
              </DialogDescription>

              {/* Benefits List */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>More Features</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Priority support</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Advanced analytics</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pb-6">
              <Button
                onClick={() => {
                  window.open('/wp-admin/admin.php?page=linkfiliate-pricing');
                  setShowModal(false);
                }}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-base"
              >
                Upgrade to Pro
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>

              <Button
                onClick={closeModal}
                variant="ghost"
                className="w-full text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 py-6 rounded-xl font-medium"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Toaster />
    </>
  )
}